import { uiURL, airlegitAddress } from 'data/common.data';
import Contact from 'modules/MainModule/components/Contact/Contact';
import Footer from 'modules/Shared/components/Footer/Footer';
import Header from 'modules/Shared/components/Header/Header';
import React, { useRef, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ILogoData } from 'models/common.data.model';
import autoCheckinIcon from '../images/auto-checkin.svg';
import flightAlertIcon from '../images/flight-alert.svg';
import { FaRegCheckSquare, FaRegSquare, FaTrash, FaPlane, FaPlaneArrival, FaSuitcaseRolling, FaClock } from 'react-icons/fa';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import './PartnerTP.scss'

const partnerDetails: ILogoData = {
    src: "/images/logos/tune-protect-logo.png",
    alt: "tuneProtect",
    link: "https://www.tuneprotect.com/emeia/our-partners/",
    width: "100px"
}
interface ChildProps {
    id: string;
}
interface IInsuranceData {
    contentIcon: any;
    contentHeader: string;
    contentDescription: React.ReactElement;
}
interface ICardData {
    id: string;
    cost: number;
    selected: [boolean] | [boolean, boolean];
    contentIcon: any;
    contentHeader: string;
    contentDescription: React.ReactElement;
    className: string;
    segments?: [string, string];
    insrData?: IInsuranceData[]
}
interface ICartData {
    id: string;
    label: string;
    cost: number;
    selected: boolean;
}
interface ISalesData {
    currency: string;
    cardData: ICardData[];
    cartData: ICartData[];
    baseCart: ICartData;
}

const CARD_DATA_ARR: ICardData[] = [
    {
        id: 'trip-1',
        cost: 3.95,
        selected: [false],
        contentIcon: flightAlertIcon,
        contentHeader: 'Flight Alerts',
        contentDescription: (
            <div className="description descriptionStyle1">
                Get WhatsApp or SMS updates on your flight's status, schedule and terminal changes, cancellations, etc.
            </div>
        ),
        className: ''
    },
    {
        id: 'trip-2',
        cost: 7.95,
        selected: [false],
        contentIcon: autoCheckinIcon,
        contentHeader: 'Auto Check-in',
        contentDescription: (
            <div className="description descriptionStyle1">
                Get boarding pass in email. Save time at the airport.
            </div>
        ),
        className: 'paddingTopSm'
    },
    {
        id: 'trip-3',
        cost: 14.95,
        selected: [false],
        contentIcon: autoCheckinIcon,
        contentHeader: 'Support Package',
        contentDescription: (
            <div className="description descriptionStyle2">
                <div>Travel Stress-Free with Priority Support. Includes:</div>
                <div>
                    <ul>
                        <li>Flight Alerts</li>
                        <li>Automated Check-ins</li>
                    </ul>
                </div>
            </div>
        ),
        className: 'paddingTop'
    },
    {
        id: 'trip-4',
        cost: 2.95,
        selected: [false],
        contentIcon: autoCheckinIcon,
        contentHeader: 'Weather Alerts',
        contentDescription: (
            <div className="description descriptionStyle1">
                Realtime monitoring and  weather updates with alerts through SMS on the day of travel.
            </div>
        ),
        className: 'paddingTop'
    },
    {
        id: 'segment-1',
        cost: 14.95,
        selected: [false, false],
        contentIcon: autoCheckinIcon,
        contentHeader: 'Flight Delay Assistance',
        contentDescription: (
            <div className="description descriptionStyle2">
                If flight is delayed more than 3 hrs, you will get a lounge pass or <sup>USD</sup> 50 cash to spend in the airport. No claim forms is required.
            </div>
        ),
        className: 'paddingTop',
        segments: ['JFK-MIA', 'MIA-JFK']
    },
    {
        id: 'segment-2',
        cost: 19.95,
        selected: [false, false],
        contentIcon: autoCheckinIcon,
        contentHeader: 'Flight Disruption Guarantee',
        contentDescription: (
            <div className="description descriptionStyle1">
                If flight is cancelled, you can jump on rebooking yourself on another airline to reach destination.
            </div>
        ),
        className: 'paddingTop',
        segments: ['JFK-MIA', 'MIA-JFK']
    }
];
const BASE_CART: ICartData = {
    id: 'base',
    label: 'Adult (x1) Air Ticket',
    cost: 389.99,
    selected: true
}
const SALES: ISalesData = {
    currency: 'USD',
    cardData: [...CARD_DATA_ARR],
    cartData: [],
    baseCart: { ...BASE_CART }
}

const responsive = {
    superLargeDesktop: {
        breakpoint: { max: 4000, min: 992 },
        items: 3
    },
    desktop: {
        breakpoint: { max: 991, min: 768 },
        items: 2
    },
    tablet: {
        breakpoint: { max: 767, min: 576 },
        items: 2
    },
    mobile: {
        breakpoint: { max: 575, min: 0 },
        items: 1
    }
};

const PartnerTP = () => {
    const [salesData, setSalesData] = React.useState<ISalesData>({ ...SALES });
    const [totalCost, setTotalCost] = React.useState<number>(salesData.baseCart.cost);
    const [innerWidth, setInnerWidth] = React.useState<number>(window.innerWidth);
    React.useEffect(() => {
        let tempSalesData: ISalesData = { ...salesData };
        tempSalesData.cartData = [{ ...tempSalesData.baseCart }];
        setSalesData(tempSalesData);
        setTotalCost(tempSalesData.baseCart.cost);
    }, []);
    React.useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });
    const handleResize = () => {
        setInnerWidth(window.innerWidth);
    };
    const toggleSelection = (index: number, selectionIndex: number) => {
        let tempSalesData: ISalesData = { ...salesData };
        tempSalesData.cardData[index].selected[selectionIndex] = !tempSalesData.cardData[index].selected[selectionIndex];
        tempSalesData.cartData = [{ ...tempSalesData.baseCart }];
        tempSalesData.cardData.forEach((card: ICardData) => {
            if (card.segments) {
                card.selected.forEach((selection: boolean, index: number) => {
                    if (selection) {
                        const temp: ICartData = {
                            id: card.id + '__' + index,
                            label: card.contentHeader + " (" + card.segments?.at(index) + ")",
                            cost: card.cost,
                            selected: true
                        }
                        tempSalesData.cartData.push(temp);
                    }
                })
            }
            if (!card.segments && card.selected[0]) {
                const temp: ICartData = {
                    id: card.id,
                    label: card.contentHeader,
                    cost: card.cost,
                    selected: true
                }
                tempSalesData.cartData.push(temp);
            }
        });
        const cost: number = tempSalesData.cartData.reduce((total: number, item: ICartData) => total + item.cost, 0);
        setTotalCost(cost);
        setSalesData(tempSalesData);
    }
    const fetchColClassName = (card: ICardData): string => {
        if (card.id.startsWith('insr')) return 'col-sm-12';
        if (card.id.startsWith('trip') || innerWidth <= 991) return 'col-sm-6'
        return 'col-sm-12';
    }
    const removeFromCartArr = (cart: ICartData) => {
        let tempSalesData: ISalesData = { ...salesData };
        tempSalesData.cartData = [...tempSalesData.cartData].filter((cartData: ICartData) => cartData.id !== cart.id);
        if (cart.id?.includes('__')) {
            const [id, index] = cart.id.split('__')
            const card = tempSalesData.cardData.find((card: ICardData) => card.id === id);
            if (card) card.selected[Number(index)] = false;
        } else {
            const card = tempSalesData.cardData.find((card: ICardData) => card.id === cart.id);
            if (card) card.selected[0] = false;
        }
        const cost: number = tempSalesData.cartData.reduce((total: number, item: ICartData) => total + item.cost, 0);
        setTotalCost(cost);
        setSalesData(tempSalesData);
    }
    const CardSelection = (card: ICardData, selection: boolean, i: number, j: number): JSX.Element => {
        return (
            <div key={card.id + '___' + j}>
                <div className={selection ? "selectionWrapper selected" : "selectionWrapper"}>
                    <span className="price">
                        <sup>{salesData.currency}</sup> {card.cost}
                    </span>
                    {!!card.segments &&
                        <span className="segment">
                            {card.segments[j]}
                        </span>
                    }
                    <span className="checkbox" onClick={() => toggleSelection(i, j)}>
                        {selection ? <FaRegCheckSquare /> : <FaRegSquare />}
                    </span>
                </div>
            </div>
        )
    }
    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <title>Tune Protect Partners</title>
                    <meta name='description' content='Get Flight Watcher to alert you on flight delays, cancellation. Choose alternative airlines, nearby airports, re-booking, or re-accommodation.' />
                </Helmet>
            </HelmetProvider>
            <Header partnerLogo={partnerDetails} />
            <div className="container text-Ancilery-Wrapper">
                <div className="text-container">
                    <div className="header-text">
                        Earn Extra Revenue with AirLegit's Ancillary Products in Partnership with <img src="/images/logos/tune-protect-logo.png" alt="tunesprotect" />
                    </div>
                    <div className="sub-header-text">
                        Your travel technology partner for ancillary revenue generation with
                        innovations, reliability and customer care.
                    </div>
                    <div className="text">
                        <p>We partner with travel agencies, OTAs, TMCs and airlines.</p>
                        <p>
                            <span>For high volume partner:</span>  <br />
                            Integrate our single API with suite of our products into your checkout path and offer personalized services to your customer.
                        </p>
                        <p>
                            <span>For moderate volume partner:</span> <br />
                            Sell ancillary products from your own branded dashboard. No integration is needed.
                        </p>
                    </div>
                    <div className="header-text">
                        Visualization
                    </div>
                    <div className="sub-header-text">
                        Below are marketing assets (mock-ups for online booking Path) of our ancillary products along with shopping cart:
                    </div>
                </div>

                <div className='ancillary-design-container'>
                    <div className='anc-design-wrapper'>
                        <div className="ancillaryDesignContainer">
                            <div className="container">
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="row cardRow">
                                            {salesData.cardData.map((card: ICardData, i: number) => {
                                                let InnerContent: React.ReactNode = <></>;
                                                if (card.id.startsWith('insr')) InnerContent = (
                                                    <div className="cardWrapper cardWrapper2">
                                                        <div className="contentHeader">
                                                            {card.contentHeader}
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-2">
                                                                <div className="contentIcon">
                                                                    <img src={card.contentIcon} alt={card.contentHeader.toLowerCase()} />
                                                                </div>
                                                            </div>
                                                            <div className="col-8">
                                                                <div className="contentWrapper">
                                                                    <div className="contentDesciption">
                                                                        {card.contentDescription}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {
                                                                card.selected.map((selection: boolean, j: number) => (
                                                                    <div key={card.id + '___' + j} className="col-2 selectionWrapperCol">
                                                                        {CardSelection(card, selection, i, j)}
                                                                    </div>
                                                                ))
                                                            }
                                                            <div className="col-12">
                                                                <div className="carouselWrapper">
                                                                    <Carousel
                                                                        swipeable={false}
                                                                        draggable={false}
                                                                        showDots={true}
                                                                        responsive={responsive}
                                                                        ssr={false}
                                                                        infinite={true}
                                                                        autoPlay={false}
                                                                        autoPlaySpeed={4000}
                                                                        keyBoardControl={true}
                                                                        customTransition="all .5"
                                                                        transitionDuration={500}
                                                                        containerClass="carousel-container"
                                                                        removeArrowOnDeviceType={[]}
                                                                        deviceType={"desktop"}
                                                                        dotListClass="custom-dot-list-style"
                                                                        itemClass="carousel-item-padding-40-px"
                                                                    >
                                                                        {
                                                                            card.insrData?.map((insr: IInsuranceData, j: number) => (
                                                                                <div key={card.id + '___' + j} className="itemDiv">
                                                                                    <div className="insuranceWrapper">
                                                                                        <div className="insrContentIcon">
                                                                                            {<insr.contentIcon />}
                                                                                        </div>
                                                                                        <div className="insrContentHeader">
                                                                                            {insr.contentHeader}
                                                                                        </div>
                                                                                        <div className="insrContentDesciption">
                                                                                            {insr.contentDescription}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        }
                                                                    </Carousel>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                                else if (card.id.startsWith('trip') || innerWidth <= 991) InnerContent = (
                                                    <div className="cardWrapper cardWrapper1">
                                                        {
                                                            card.selected.map((selection: boolean, j: number) => (
                                                                <div key={card.id + '___' + j}>
                                                                    {CardSelection(card, selection, i, j)}
                                                                </div>
                                                            ))
                                                        }
                                                        <div className="contentWrapper">
                                                            <div className="contentIcon">
                                                                <img src={card.contentIcon} alt={card.contentHeader.toLowerCase()} />
                                                            </div>
                                                            <div className="contentHeader">
                                                                {card.contentHeader}
                                                            </div>
                                                            <div className="contentDesciption">
                                                                {card.contentDescription}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                                else InnerContent = (
                                                    <div className="cardWrapper cardWrapper2">
                                                        <div className="contentHeader">
                                                            {card.contentHeader}
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-2">
                                                                <div className="contentIcon">
                                                                    <img src={card.contentIcon} alt={card.contentHeader.toLowerCase()} />
                                                                </div>
                                                            </div>
                                                            <div className="col-6">
                                                                <div className="contentWrapper">
                                                                    <div className="contentDesciption">
                                                                        {card.contentDescription}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {
                                                                card.selected.map((selection: boolean, j: number) => (
                                                                    <div key={card.id + '___' + j} className="col-2 selectionWrapperCol">
                                                                        {CardSelection(card, selection, i, j)}
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    </div>
                                                );
                                                return (
                                                    <div key={card.id} className={fetchColClassName(card) + " " + card.className}>
                                                        {InnerContent}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                    <div className="col-md-4 shoppingCartWrapper">
                                        <div className="shoppingCart">
                                            <div className="contentHeader">
                                                Shopping Cart (Price Details)
                                            </div>
                                            <table>
                                                <tbody>
                                                    {salesData.cartData.map((cart: ICartData, i: number) => (
                                                        <tr key={cart.id} className="">
                                                            <td className="label">
                                                                {(i !== 0) &&
                                                                    <span className="trash" onClick={() => removeFromCartArr(cart)} title="Remove"><FaTrash /></span>
                                                                }
                                                                <span>{cart.label}</span>
                                                            </td>
                                                            <td className="cost">
                                                                <sup>{salesData.currency}</sup> {cart.cost}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    <tr className="total">
                                                        <td className="label">
                                                            Total Cost
                                                        </td>
                                                        <td className="cost">
                                                            <sup>{salesData.currency}</sup> {totalCost?.toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Contact />
            <Footer />
        </>
    )
}
export default PartnerTP;